import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Modal, Progress, Space, Table, Tabs, Tag, Typography } from 'antd';
import { adminConfig, projectAdmin } from '../list-view/admin-config';
import { useLocalList } from '../list-view/use-local-list';
import { apiGet, apiPost, clearLearningToken, fileBase64, learningRequest } from '../api/client';
import { FilteredTable } from '../list-view/list-view';
import { detailConfig, projectCourseProgress, projectLearning, trackingConfig } from '../list-view/tracking-config';

type Course = { id: string; title: string; duration: number; published: boolean; uploaded?: boolean };
type CourseProgress = {
  courseId: string;
  title: string;
  duration: number;
  coveredSeconds: number;
  watchedSeconds: number;
  lastPosition: number;
  lastLearnedAt?: string;
  completedAt?: string;
};
type Enrollment = {
  progress: number;
  courses: CourseProgress[];
  completedAt?: string;
  consentAt?: string;
  todo: { status: string; error?: string };
};
type LearningData = { name: string; employeeType: string; courses: Course[]; enrollment?: Enrollment };
type DashboardRow = {
  onboardDate?: string;
  learningDueDate?: string;
  lastLearnedAt?: string;
  todoCreationStatus?: string;
  planId: string;
  name: string;
  employeeType: string;
  department?: string;
  accessStatus?: 'READY' | 'ACCOUNT_LINK_REQUIRED';
  enrollment?: Enrollment;
};
const message = (error: unknown) => (error instanceof Error ? error.message : '操作失败，请重试');
export const learningTime = (seconds: number) =>
  `${Math.floor((seconds || 0) / 60)}分${Math.floor((seconds || 0) % 60)}秒`;
const percent = (p: CourseProgress) =>
  p.duration ? Math.min(100, Math.round((p.coveredSeconds / p.duration) * 100)) : 0;
const todoLabel = (e: Enrollment) =>
  e.todo.status === 'SUCCEEDED'
    ? '钉钉待办已关闭'
    : !e.consentAt
      ? '未授权关闭钉钉待办'
      : !e.completedAt
        ? '学完后同步关闭待办'
        : e.todo.status === 'FAILED'
          ? '钉钉待办关闭失败'
          : '钉钉待办待同步';

export function LearningPage() {
  const [data, setData] = useState<LearningData>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [courseId, setCourseId] = useState('');
  const reload = async () => {
    try {
      setData(await learningRequest<LearningData>('/me'));
      setError('');
    } catch (e) {
      setError(message(e));
    }
  };
  useEffect(() => {
    void reload();
  }, []);
  const action = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const e = data?.enrollment;
  const watchCourse = (id: string) => {
    setCourseId(id);
    if (id === courseId) document.getElementById('learning-player')?.scrollIntoView?.({ block: 'start' });
  };
  return (
    <main style={{ maxWidth: 1100, margin: '32px auto', padding: 20 }}>
      <Typography.Title level={2}>新人学习中心</Typography.Title>
      <Typography.Paragraph>
        逐课观看并保存真实播放进度。重复观看不重复累计覆盖率，跳过的片段不计入完成。
      </Typography.Paragraph>
      {error && <Alert showIcon type="error" message={error} />}
      {!data ? (
        <Button
          loading={busy}
          type="primary"
          onClick={() =>
            void action(async () => {
              const result = await learningRequest<{ loginUrl: string }>('/login', {});
              window.location.href = result.loginUrl;
            })
          }
        >
          使用钉钉登录学习
        </Button>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space>
            <Typography.Text>
              {data.name} · {data.employeeType}
            </Typography.Text>
            <Button onClick={() => void reload()}>刷新进度</Button>
            <Button
              onClick={() =>
                void action(async () => {
                  await learningRequest('/logout', {});
                  clearLearningToken();
                  setData(undefined);
                  setCourseId('');
                })
              }
            >
              退出学习
            </Button>
          </Space>
          {!data.courses.length || data.courses.some((c) => !c.published) ? (
            <Alert type="warning" showIcon message="课程尚未全部发布，请等待 HR 配置视频，不会把打开链接当作学完。" />
          ) : null}
          <Card title={e?.completedAt ? '必修视频学习完成' : '我的学习进度'}>
            <Progress percent={e?.progress ?? 0} />
            <Typography.Paragraph>
              已完成 {e?.courses.filter((c) => c.completedAt).length ?? 0} / {data.courses.length} 门 · 有效观看{' '}
              {learningTime(e?.courses.reduce((sum, c) => sum + c.watchedSeconds, 0) ?? 0)}
            </Typography.Paragraph>
            {e && (
              <>
                <Tag>{todoLabel(e)}</Tag>
                {e.todo.error && <Alert message={e.todo.error} type="warning" />}
              </>
            )}
            {!e?.consentAt && (
              <Checkbox checked={consent} onChange={(event) => setConsent(event.target.checked)}>
                我确认：完成全部必修视频后，系统同步关闭本人钉钉学习待办
              </Checkbox>
            )}
            {!e && (
              <Button
                type="primary"
                loading={busy}
                disabled={!data.courses.length || data.courses.some((c) => !c.published)}
                onClick={() =>
                  void action(async () => {
                    await learningRequest('/enroll', { consent });
                    await reload();
                  })
                }
              >
                开始学习
              </Button>
            )}
            {e && !e.consentAt && (
              <Button
                disabled={!consent}
                onClick={() =>
                  void action(async () => {
                    await learningRequest('/enroll', { consent: true });
                    await reload();
                  })
                }
              >
                确认完成后关闭待办
              </Button>
            )}
            {e?.completedAt && e.consentAt && e.todo.status === 'FAILED' && (
              <Button
                loading={busy}
                onClick={() =>
                  void action(async () => {
                    await learningRequest('/retry-todo', { confirmed: true });
                    await reload();
                  })
                }
              >
                确认重试关闭待办
              </Button>
            )}
          </Card>
          <Table
            rowKey="id"
            pagination={false}
            dataSource={data.courses}
            columns={[
              { title: '必修课程', dataIndex: 'title' },
              { title: '视频时长', render: (_, c) => (c.duration ? learningTime(c.duration) : '待配置') },
              {
                title: '学习进度',
                render: (_, c) => {
                  const p = e?.courses.find((p) => p.courseId === c.id);
                  return p ? <Progress size="small" percent={percent(p)} /> : '未开始';
                }
              },
              {
                title: '有效观看',
                render: (_, c) => learningTime(e?.courses.find((p) => p.courseId === c.id)?.watchedSeconds ?? 0)
              },
              {
                title: '操作',
                render: (_, c) => (
                  <Button disabled={!e || !c.published} onClick={() => watchCourse(c.id)}>
                    观看视频
                  </Button>
                )
              }
            ]}
          />
          {courseId && (
            <LearningPlayer
              key={courseId}
              courseId={courseId}
              title={data.courses.find((c) => c.id === courseId)?.title ?? ''}
              onProgress={(enrollment) => setData((current) => (current ? { ...current, enrollment } : current))}
            />
          )}
        </Space>
      )}
    </main>
  );
}

export function LearningPlayer({
  courseId,
  title,
  onProgress
}: {
  courseId: string;
  title: string;
  onProgress: (e: Enrollment) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const player = useRef<HTMLDivElement>(null);
  const playback = useRef<{ id: string; seq: number; failed: boolean; queue: Promise<void> } | undefined>(undefined);
  const alive = useRef(true);
  const lastReportAt = useRef(0);
  const [session, setSession] = useState<{ id: string; url: string; position: number; duration: number }>();
  const [rate, setRate] = useState(1);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const send = (event: 'play' | 'tick' | 'pause' | 'seek') => {
    const current = playback.current,
      v = video.current;
    if (!current || !v || current.failed || !Number.isFinite(v.currentTime)) return;
    lastReportAt.current = Date.now();
    const body = { sessionId: current.id, seq: ++current.seq, position: v.currentTime, rate: v.playbackRate, event };
    setSaving(true);
    current.queue = current.queue.then(async () => {
      if (current.failed) return;
      try {
        const result = await learningRequest<Enrollment>('/heartbeat', body);
        if (alive.current && playback.current === current) {
          onProgress(result);
          setSaving(false);
        }
      } catch (e) {
        current.failed = true;
        if (alive.current && playback.current === current) {
          v.pause();
          setSaving(false);
          setError(`进度未保存：${message(e)}。请重新加载播放器，从已保存位置继续。`);
        }
      }
    });
  };
  const reportPlayback = (flush = false) => {
    const v = video.current;
    if (v && !v.paused && !v.seeking && v.readyState >= 3 && (flush || Date.now() - lastReportAt.current >= 10000))
      send('tick');
  };
  useEffect(() => {
    alive.current = true;
    const timer = window.setInterval(() => reportPlayback(), 10000);
    const saveVisiblePosition = () => reportPlayback(true);
    document.addEventListener('visibilitychange', saveVisiblePosition);
    return () => {
      send('pause');
      alive.current = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', saveVisiblePosition);
    };
  }, [courseId]);
  const start = useCallback(async () => {
    setStarting(true);
    setError('');
    video.current?.pause();
    if (playback.current) await playback.current.queue;
    try {
      const result = await learningRequest<{ id: string; url: string; position: number; duration: number }>(
        `/start/${encodeURIComponent(courseId)}`,
        {}
      );
      if (alive.current) {
        playback.current = { id: result.id, seq: 0, failed: false, queue: Promise.resolve() };
        setSession(result);
      }
    } catch (e) {
      if (alive.current) setError(message(e));
    } finally {
      if (alive.current) setStarting(false);
    }
  }, [courseId]);
  useEffect(() => {
    player.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    void start();
  }, [start]);
  return (
    <div id="learning-player" ref={player}>
      <Card title={title}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {error && <Alert type="error" message={error} />}
          {starting && <Typography.Text>正在加载视频…</Typography.Text>}
          {error && <Button onClick={() => void start()}>重新加载视频</Button>}
          {session && (
            <>
              <video
                key={session.id}
                ref={video}
                src={session.url}
                controls
                playsInline
                preload="metadata"
                style={{ width: '100%', maxHeight: 600, background: '#111' }}
                onLoadedMetadata={() => {
                  const v = video.current;
                  if (!v) return;
                  if (!Number.isFinite(v.duration) || Math.abs(v.duration - session.duration) > 1) {
                    playback.current!.failed = true;
                    setError('视频时长校验不一致，已停止计分，请联系 HR');
                    return;
                  }
                  v.currentTime = Math.min(session.position, v.duration);
                  v.playbackRate = rate;
                }}
                onPlay={() => {
                  if (playback.current?.failed) video.current?.pause();
                  else send('play');
                }}
                onTimeUpdate={() => reportPlayback()}
                onPause={() => send('pause')}
                onEnded={() => send('pause')}
                onSeeking={() => send('seek')}
                onSeeked={() => {
                  if (!video.current?.paused) send('play');
                }}
                onWaiting={() => send('pause')}
                onPlaying={() => send('play')}
                onRateChange={() => {
                  send('seek');
                  if (!video.current?.paused) send('play');
                }}
                onError={() => {
                  send('pause');
                  setError('视频加载失败，请重新加载视频；已保存的进度不会丢失。');
                }}
              />
              <label htmlFor="learning-playback-rate">播放速度：</label>
              <select
                id="learning-playback-rate"
                aria-label="播放速度"
                value={rate}
                onChange={(event) => {
                  const nextRate = Number(event.target.value);
                  setRate(nextRate);
                  if (video.current) video.current.playbackRate = nextRate;
                }}
              >
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map((option) => (
                  <option key={option} value={option}>
                    {option} 倍
                  </option>
                ))}
              </select>
            </>
          )}
          <Typography.Text type="secondary">
            {saving
              ? '正在保存进度…'
              : '支持边播放边记笔记，切换窗口仍会记录实际播放时长；暂停、缓冲和跳过的片段不计入。每门课覆盖达到 95% 后完成。'}
          </Typography.Text>
        </Space>
      </Card>
    </div>
  );
}

export function LearningDashboard({ admin }: { admin: boolean }) {
  const { items: rows, error, load: reload } = useLocalList<DashboardRow>('/hr/learning');
  return (
    <section>
      <Typography.Title level={2}>新人学习追踪</Typography.Title>
      <Tabs
        items={[
          {
            key: 'progress',
            label: '学习看板',
            children: (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  type="info"
                  message="覆盖正式员工和实习生。观看时长与钉钉待办状态分别记录；历史链接访问不能推算成已学完。"
                />
                <Button onClick={() => void reload()}>刷新本地进度</Button>
                <FilteredTable<DashboardRow>
                  scroll={{ x: 1000 }}
                  config={trackingConfig('learning')}
                  project={(row, index) => projectLearning(row, index)}
                  error={error}
                  rowKey="planId"
                  dataSource={rows}
                  columns={[
                    { title: '员工', dataIndex: 'name' },
                    { title: '部门', dataIndex: 'department' },
                    {
                      title: '员工类型',
                      dataIndex: 'employeeType'
                    },
                    {
                      title: '账号关联',
                      render: (_, row) =>
                        row.accessStatus === 'ACCOUNT_LINK_REQUIRED' ? (
                          <Tag color="warning">待关联钉钉账号</Tag>
                        ) : (
                          '已关联'
                        )
                    },
                    {
                      title: '总进度',
                      render: (_, row) => <Progress size="small" percent={row.enrollment?.progress ?? 0} />
                    },
                    {
                      title: '有效观看',
                      render: (_, row) =>
                        learningTime(row.enrollment?.courses.reduce((sum, c) => sum + c.watchedSeconds, 0) ?? 0)
                    },
                    {
                      title: '学习状态 / 待办状态',
                      render: (_, row) =>
                        row.accessStatus === 'ACCOUNT_LINK_REQUIRED' ? (
                          '缺少钉钉 userId，关联后员工才能登录学习和接收待办。'
                        ) : row.enrollment ? (
                          <Space direction="vertical">
                            <span>{projectLearning(row, 0).facets.status}</span>
                            <span>{todoLabel(row.enrollment)}</span>
                            {row.enrollment.todo.error && (
                              <Typography.Text type="danger">{row.enrollment.todo.error}</Typography.Text>
                            )}
                          </Space>
                        ) : (
                          '未开始'
                        )
                    }
                  ]}
                  expandable={{
                    expandedRowRender: (row) => (
                      <FilteredTable<CourseProgress>
                        scroll={{ x: 800 }}
                        config={detailConfig('courses', row.planId)}
                        project={projectCourseProgress}
                        rowKey="courseId"
                        pagination={false}
                        dataSource={row.enrollment?.courses ?? []}
                        columns={[
                          { title: '课程', dataIndex: 'title' },
                          { title: '进度', render: (_, p) => `${percent(p)}%` },
                          {
                            title: '已看内容 / 视频时长',
                            render: (_, p) => `${learningTime(p.coveredSeconds)} / ${learningTime(p.duration)}`
                          },
                          { title: '观看时长', render: (_, p) => learningTime(p.watchedSeconds) },
                          {
                            title: '最近学习',
                            render: (_, p) =>
                              p.lastLearnedAt ? new Date(p.lastLearnedAt).toLocaleString('zh-CN') : '未开始'
                          },
                          { title: '状态', render: (_, p) => (p.completedAt ? '已完成' : '未完成') }
                        ]}
                      />
                    )
                  }}
                />
              </Space>
            )
          },
          ...(admin ? [{ key: 'courses', label: '视频课程管理', children: <CourseManagement /> }] : [])
        ]}
      />
    </section>
  );
}

function CourseManagement() {
  const [courses, setCourses] = useState<Course[]>([]),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(''),
    [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<{ id: string; title: string; url: string; token: string }>(),
    [played, setPlayed] = useState(false),
    [confirmed, setConfirmed] = useState(false);
  const reload = async () => setCourses(await apiGet<Course[]>('/hr/learning/courses'));
  useEffect(() => {
    void reload().catch((e) => setError(message(e)));
  }, []);
  const upload = async (courseId: string, file: File) => {
    if (!file.name.toLowerCase().endsWith('.mp4')) {
      setError('请选择 MP4 视频');
      return;
    }
    setBusy(courseId);
    setError('');
    setProgress(0);
    let uploadId = '';
    try {
      const result = await apiPost<{ id: string; chunkSize: number }>('/hr/learning/uploads', {
        courseId,
        size: file.size
      });
      uploadId = result.id;
      for (let offset = 0; offset < file.size; offset += result.chunkSize) {
        const data = await fileBase64(file.slice(offset, offset + result.chunkSize));
        await apiPost(`/hr/learning/uploads/${result.id}/chunk`, { offset, data });
        setProgress(Math.round((Math.min(file.size, offset + result.chunkSize) / file.size) * 100));
      }
      await apiPost(`/hr/learning/uploads/${result.id}/finish`, {});
      uploadId = '';
      await reload();
    } catch (e) {
      setError(message(e));
      if (uploadId) await apiPost(`/hr/learning/uploads/${uploadId}/cancel`, {}).catch(() => undefined);
    } finally {
      setBusy('');
    }
  };
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Alert
        type="info"
        message="保留钉钉原件，上传 MP4 副本后预览并确认发布。单个视频最大 2 GB；已发布或已有学习记录的视频不能覆盖。全部课程发布后员工才可开始计分学习。"
      />
      {error && <Alert type="error" message={error} />}
      {busy && <Progress percent={progress} />}
      <FilteredTable<Course>
        config={adminConfig('courses')}
        project={(row, index) => projectAdmin('courses', row, index)}
        rowKey="id"
        pagination={false}
        dataSource={courses}
        columns={[
          { title: '课程', dataIndex: 'title' },
          { title: '状态', render: (_, c) => (c.published ? '已发布' : c.uploaded ? '待预览发布' : '待上传视频') },
          { title: '视频时长', render: (_, c) => learningTime(c.duration) },
          {
            title: '上传视频',
            render: (_, c) => (
              <input
                aria-label={`上传${c.title}`}
                type="file"
                accept="video/mp4,.mp4"
                disabled={Boolean(busy) || c.published}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void upload(c.id, file);
                  event.target.value = '';
                }}
              />
            )
          },
          {
            title: '预览 / 发布',
            render: (_, c) => (
              <Button
                disabled={!c.uploaded || Boolean(busy)}
                onClick={async () => {
                  try {
                    const result = await apiPost<{ url: string; token: string }>(
                      `/hr/learning/courses/${c.id}/preview`,
                      {}
                    );
                    setPlayed(false);
                    setConfirmed(false);
                    setPreview({ ...result, id: c.id, title: c.title });
                  } catch (e) {
                    setError(message(e));
                  }
                }}
              >
                预览视频
              </Button>
            )
          }
        ]}
      />
      <Modal
        width={900}
        title={preview?.title}
        open={Boolean(preview)}
        onCancel={() => setPreview(undefined)}
        footer={null}
        destroyOnHidden
      >
        {preview && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <video
              controls
              playsInline
              src={preview.url}
              style={{ width: '100%' }}
              onTimeUpdate={(event) => {
                if (event.currentTarget.currentTime > 0 && !event.currentTarget.seeking) setPlayed(true);
              }}
              onError={() => {
                setPlayed(false);
                setError('预览播放失败，不能发布');
              }}
            />
            {!courses.find((c) => c.id === preview.id)?.published && (
              <>
                <Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)}>
                  我已实际播放，确认画面、声音和时长正确，并授权发布给正式员工及实习生
                </Checkbox>
                <Button
                  type="primary"
                  disabled={!played || !confirmed || Boolean(busy)}
                  onClick={async () => {
                    setBusy(preview.id);
                    try {
                      await apiPost(`/hr/learning/courses/${preview.id}/publish`, {
                        confirmed: true,
                        previewToken: preview.token
                      });
                      setPreview(undefined);
                      await reload();
                    } catch (e) {
                      setError(message(e));
                    } finally {
                      setBusy('');
                    }
                  }}
                >
                  确认发布课程
                </Button>
              </>
            )}
          </Space>
        )}
      </Modal>
    </Space>
  );
}
